import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCall } from '../contexts/CallContext';
import { useUser } from '../contexts/UserContext';
import { ref, onValue, off, set, update, remove, get } from 'firebase/database';
import { database } from '../constants/firebase';
import OutgoingCallPopup from './OutgoingCallPopup';
import IncommingCallPopup from './IncommingCallPopup';
import { LuPhoneOff as FiPhoneOff } from "react-icons/lu";
import { DailyAudio } from '@daily-co/daily-react';
import { LuMinimize as FaCompress } from "react-icons/lu";

const GlobalCallPopup = () => {
    const {
        callObject,
        callStatus,
        setCallStatus,

        isIncomingCall,
        setIsIncomingCall,

        isOutgoingCall,
        setIsOutgoingCall,

        activeCallData,
        setActiveCallData,

        isOnCall,
        setIsOnCall,

        setUserId,
        userId,
        participantIds,
        localSessionId
    } = useCall();

    const { currentUser } = useUser();

    const [callUser, setCallUser] = useState(null);
    const [isAcceptingCall, setIsAcceptingCall] = useState(false);
    const [isCallMinimized, setIsCallMinimized] = useState(false);
    const [handledByApp, setHandledByApp] = useState(false);
    const prevVideoRef = useRef(null);
    const hasEndedRef = useRef(false);

    // ─────────────────────────────
    // RESET STATE
    // ─────────────────────────────
    const resetCallState = useCallback(() => {
        setIsIncomingCall(false);
        setIsOutgoingCall(false);
        setIsOnCall(false);
        setCallStatus("idle");
        setActiveCallData(null);
        setCallUser(null);
        setUserId(null);
        setIsCallMinimized(false);
    }, []);

    // ─────────────────────────────
    // CALL SESSION LISTENER (MAIN FIX)
    // ─────────────────────────────
    useEffect(() => {
        if (!currentUser?.uid) return;
        if (window.isInApp) return; // 🛑 DISABLE IN-APP LISTENER (RN HANDOFF)

        const sessionRef = ref(database, `callSessions`);


        const unsubscribe = onValue(sessionRef, (snap) => {
            const data = snap.val();
            if (!data) return resetCallState();

            const sessionEntry = Object.entries(data).find(([id, s]) =>
                s.from === currentUser.uid || s.to === currentUser.uid
            );

            if (!sessionEntry) return resetCallState();

            const [sessionId, session] = sessionEntry;

            setActiveCallData({ ...session, callId: sessionId });

            const isIncoming =
                session.to === currentUser.uid &&
                session.status === "ringing";

            const isOutgoing =
                session.from === currentUser.uid &&
                session.status === "ringing";

            setIsIncomingCall(isIncoming);
            setIsOutgoingCall(isOutgoing);

            if (session.status === "connected") {
                setIsOnCall(true);
                setCallStatus("connected");
                setHandledByApp(!!session.handledByApp);
            }
            if (session.status === "declined") {
                resetCallState();
                remove(ref(database, `callSessions/${sessionId}`));
            }

            if (session.status === "ended") {
                resetCallState();
                callObject?.leave();
                setTimeout(async () => {
                    remove(ref(database, `callSessions/${sessionId}`));
                }, 5000);

            }

            // 🔥 SAFE VIDEO SYNC (FIXED)
            if (session.isVideo !== prevVideoRef.current) {
                prevVideoRef.current = session.isVideo;

                console.log('[CALL] Video mode changed:', session.isVideo);

                callObject?.setLocalVideo(!!session.isVideo);
            }

            const other =
                session.from === currentUser.uid
                    ? session.to
                    : session.from;

            setUserId(other);
            console.log("Other: ", other);
        });

        return () => off(sessionRef);
    }, [currentUser?.uid]);


    useEffect(() => {
        if (!currentUser?.uid) return;
        if (window.isInApp) return; // 🛑 DISABLE IN-APP LISTENER (RN HANDOFF)

        const groupRef = ref(database, "groupCallSessions");

        const unsubscribe = onValue(groupRef, (snap) => {
            const data = snap.val();
            if (!data) return;

            let active = null;
            let callId = null;

            Object.entries(data).forEach(([id, session]) => {
                if (session.participants?.[currentUser.uid]) {
                    active = session;
                    callId = id;
                }
            });

            if (!active) return;

            const myParticipant = active.participants?.[currentUser.uid];

            setActiveCallData({ ...active, callId });

            const isIncoming =
                myParticipant?.status === "ringing" &&
                active.from !== currentUser.uid;

            const isOutgoing =
                active.from === currentUser.uid &&
                active.status === "ringing";

            setIsIncomingCall(isIncoming);
            setIsOutgoingCall(isOutgoing);

            // ❌ REMOVE THIS (THIS WAS CAUSING AUTO JOIN)
            if (active.status === "connected") {
                setIsOnCall(true);
                setCallStatus("connected");
            }

            // ✅ ONLY TRUST PERSONAL STATE
            // if (myParticipant?.status === "joined") {
            //     setIsOnCall(true);
            //     setCallStatus("connected");
            // }

            if (myParticipant?.status === "left") {
                resetCallState();
                callObject?.leave();
            }

            if (active.status === "ended") {
                resetCallState();
                callObject?.leave();
                remove(ref(database, `groupCallSessions/${callId}`));
            }

            // 🔥 VIDEO SYNC SAFE
            if (active.isVideo !== prevVideoRef.current) {
                prevVideoRef.current = active.isVideo;
                callObject?.setLocalVideo(!!active.isVideo);
            }

            const otherUser = Object.keys(active.participants || {}).find(
                (uid) => uid !== currentUser.uid
            );

            setUserId(otherUser || null);
        });

        return () => off(groupRef);
    }, [currentUser?.uid]);

    // ─────────────────────────────
    // FETCH USER
    // ─────────────────────────────
    useEffect(() => {
        console.log("[Effect] userId changed:", userId);

        const userRef = ref(database, `Users/${userId}`);
        const groupRef = ref(database, `Groups/${activeCallData?.groupId}`);

        if (activeCallData?.groupId) {
            onValue(groupRef, (snapshot) => {
                console.log("[Effect] onValue group snapshot:", snapshot.val());
                if (snapshot.exists()) {
                    setCallUser(snapshot.val());
                }
            });
        } else if (userId) {
            onValue(userRef, (snapshot) => {
                console.log("[Effect] onValue user snapshot:", snapshot.val());
                if (snapshot.exists()) {
                    setCallUser(snapshot.val());
                }
            });
        }

        if (!userId && !activeCallData?.groupId) {
            setCallUser(null);
            return;
        }


        return () => {
            console.log("[Cleanup] off userRef");
            off(userRef);
            console.log("[Cleanup] off groupRef");
            off(groupRef);
        };
    }, [activeCallData, userId]);


    // ─────────────────────────────
    // ACCEPT CALL
    // ─────────────────────────────
    const handleAcceptCall = async (muted) => {
        try {
            setIsAcceptingCall(true);

            const callId = activeCallData.callId;

            await update(ref(database, `callSessions/${callId}`), {
                status: "connected",
                [`accepted/${currentUser.uid}`]: true,
                [`participants/${currentUser.uid}`]: {
                    status: 'joined',
                },
            });

            await callObject.join({ url: activeCallData.url });

            callObject.setLocalVideo(activeCallData.isVideo);
            callObject.setLocalAudio(!muted);

            setCallStatus("connected");
            setIsOnCall(true);
            setIsIncomingCall(false);
            setIsOutgoingCall(true);


        } catch (e) {
            console.error("Accept error:", e);
        } finally {
            setIsAcceptingCall(false);
        }
    };


    const handleAcceptGroupCall = async (muted) => {
        try {
            const callId = activeCallData.callId;

            await update(ref(database, `groupCallSessions/${callId}`), {
                [`participants/${currentUser.uid}`]: {
                    uid: currentUser.uid,
                    status: "joined",
                },
            });

            await callObject.join({ url: activeCallData.url });

            callObject.setLocalVideo(activeCallData.isVideo);
            callObject.setLocalAudio(!muted);

            setIsOnCall(true);
            setCallStatus("connected");

        } catch (e) {
            console.error(e);
        }
    };

    // ─────────────────────────────
    // DECLINE CALL
    // ─────────────────────────────
    const handleDeclineCall = async () => {
        try {
            const callId = activeCallData?.callId;
            if (!callId) return;

            await update(ref(database, `callSessions/${callId}`), {
                status: "declined",
                declinedBy: currentUser.uid
            });

            resetCallState();

        } catch (e) {
            console.error(e);
        }
    };

    const handleDeclineGroupCall = async () => {
        const callId = activeCallData?.callId;

        await update(ref(database, `groupCallSessions/${callId}`), {
            [`participants/${currentUser.uid}/status`]: "declined",
        });

        resetCallState();
    };

    // ─────────────────────────────
    // END CALL
    // ─────────────────────────────
    const handleEndCall = async () => {
        try {
            if (hasEndedRef.current) return;
            hasEndedRef.current = true;

            const callId = activeCallData?.callId;


            await update(ref(database, `callSessions/${callId}`), {
                status: "ended",
                endedBy: currentUser.uid,
                endedAt: Date.now()
            });

            await callObject?.leave();

            resetCallState();

        } catch (e) {
            console.error(e);
        } finally {
            hasEndedRef.current = false;
        }
    };

    const handleGroupEndCall = async () => {
        const callId = activeCallData?.callId;

        if (!callId) return;

        const sessionRef = ref(database, `groupCallSessions/${callId}`);

        // 1️⃣ mark current user as left
        await update(sessionRef, {
            [`participants/${currentUser.uid}/status`]: "left",
        });

        // 2️⃣ get latest snapshot to check remaining participants
        const snap = await get(sessionRef);
        const data = snap.val();

        if (!data?.participants) {
            await callObject.leave();
            resetCallState();
            return;
        }

        // 3️⃣ check if ANY active participant remains
        const remaining = Object.values(data.participants || {}).filter(
            (p) => p?.status === "joined"
        );

        // 4️⃣ if no one left → destroy call completely
        if (remaining.length === 0) {
            await remove(sessionRef); // 🔥 delete call session
        }

        // 5️⃣ leave Daily room
        await callObject.leave();

        // 6️⃣ reset UI state
        resetCallState();
    };

    // ─────────────────────────────
    // TOGGLE MEDIA
    // ─────────────────────────────
    const handleToggleMute = (muted) => {
        callObject?.setLocalAudio(!muted);
    };

    const handleToggleVideo = async (enabled) => {
        callObject?.setLocalVideo(enabled);

        const callId = activeCallData?.callId;
        if (callId) {
            await update(ref(database, `callSessions/${callId}`), {
                isVideo: enabled
            });
        }
    };

    // ─────────────────────────────
    // DAILY EVENT SYNC
    // ─────────────────────────────
    useEffect(() => {
        if (!callObject) return;

        const handler = async () => {
            setCallStatus("connected");
            setIsOnCall(true);
        };

        if (callObject) {
            callObject.on("joined-meeting", handler);
        }

        return () => {
            callObject?.off("joined-meeting", handler);
        };
    }, [callObject]);

    // ─────────────────────────────
    // RENDER
    // ─────────────────────────────
    if (!activeCallData) return null;

    const avatar =
        callUser?.profilePic ||
        "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media";

    return (
        <>
            {!isCallMinimized && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">

                    {isIncomingCall && !isOnCall && (
                        <IncommingCallPopup
                            user={callUser}
                            onAccept={(isMuted) => {
                                if (activeCallData.groupId) {
                                    handleAcceptGroupCall(isMuted)
                                } else {
                                    handleAcceptCall(isMuted)
                                }
                            }}
                            onDecline={() => {
                                if (activeCallData.groupId) {
                                    handleDeclineGroupCall()
                                } else {
                                    handleDeclineCall()
                                }
                            }}
                            callType={activeCallData.isVideo ? "video" : "audio"}
                            isVideoEnabled={activeCallData.isVideo}
                            isAcceptingCall={isAcceptingCall}
                        />
                    )}

                    {(isOutgoingCall || isOnCall) && (
                        <OutgoingCallPopup
                            user={callUser}
                            callStatus={callStatus}
                            onToggleMute={handleToggleMute}
                            onToggleVideo={handleToggleVideo}
                            initialMuted={false}
                            initialVideo={activeCallData?.isVideo || false}
                            onCancel={() => {
                                if (activeCallData.groupId) {
                                    handleGroupEndCall()
                                } else {
                                    handleEndCall()
                                }
                            }}
                            participantIds={participantIds}
                            localSessionId={localSessionId}
                            handledByApp={handledByApp}
                            onMinimize={() => {
                                if (!activeCallData?.groupId) { setIsCallMinimized(true); }
                            }}
                        />
                    )}

                </div>
            )}

            {callStatus === "connected" && (
                <DailyAudio />
            )}

            {isCallMinimized && (
                <div className="fixed bottom-4 right-4 z-[9999] bg-black text-white p-3 rounded-full flex gap-2 items-center">
                    <img src={avatar} className="w-8 h-8 rounded-full" />
                    <button onClick={() => setIsCallMinimized(false)}>
                        <FaCompress />
                    </button>
                    <button onClick={handleEndCall}>
                        <FiPhoneOff />
                    </button>
                </div>
            )}
        </>
    );
};

export default GlobalCallPopup;